import { FormGroup, Validators } from '@angular/forms';
import { Control } from './pennsylvania/pennsylvania-model';
import { PipBaseRulesService } from './services/pip-base-rules.service';
import { Directive, inject } from '@angular/core';
import { UserRole } from '../../config/constants';
import { NotificationService } from '../../_services/notification/notification.service';
import { RequestService } from '../../_services/request/request.service';
import { LoadingService } from 'axis-components';
import { startWith } from 'rxjs';

@Directive()
export class PipBaseStateComponent<BaseRules, TextValues> {
  reactiveForm: FormGroup;
  dynamicRules = [];
  baseRules: BaseRules;
  controls: Control[];
  textValues: TextValues;
  isReviewPage: boolean = false;
  selectedPremium: any;
  state: string;
  showPremium: boolean = false;
  static stopLoading: boolean = false;
  static loadedStatesCount: number = 0;
  static totalStatesCount: number = 0;
  public baseRulesService = inject(PipBaseRulesService);
  public requestService = inject(RequestService);
  public notificationService = inject(NotificationService);
  private loadingService = inject(LoadingService);
  outstandingLoadingService: any;

  getRadioButtons(control: any) {
    return !this.baseRules[control.baseRuleKey]?.includes('show');
  }

  setupControlEvents() {
    if (this.baseRules && this.baseRules['hasOutstanding']) {
      this.setValidation();
    }
    this.reactiveForm.valueChanges.subscribe((res) => {
      const controls = Object.values(this.reactiveForm.controls);

      // Check if any control is valid and has been interacted with
      const anyValid = controls.some(
        (control: any) => control.valid && (control.dirty || control.touched),
      );
      // Only log on the first change
      if (
        !this.notificationService.PIPOutstandingNotificationFlag &&
        this.requestService.getUserRole() === UserRole.BROKER &&
        anyValid
      ) {
        this.notificationService.PIPOutstandingNotificationFlag = true;
        this.notificationService.showNotification(
          'Please note that the insured must fill out this form using the link sent to their inbox. All changes made to this view will not be saved.',
          'warning',
          'Selections Will Not Be Saved',
        );
      }
      this.hookForAllEvents(res);
    });
    this.controls.forEach((control: Control) => {
      if (control.hasEvent) {
        let isInitialLoad = true;
        this.reactiveForm
          .get(control.formControlName)
          .valueChanges.pipe(
            startWith(this.reactiveForm.get(control.formControlName).value),
          )
          .subscribe((res) => {
            if (res && !this.baseRulesService.isContinueButtonClicked) {
              const hasExistingValue =
                (res?.value !== undefined && res?.value !== null) ||
                (typeof res === 'string' && res !== null);
              if (hasExistingValue) {
                this.eventHook(res.value ?? res);
                // Only reset during user interactions, not during initial load/navigation
                if (!isInitialLoad) {
                  this.setDynamicRules(res.value ?? res);
                }
              }
            }
            // Set flag to false after first emission (initial load)
            isInitialLoad = false;
          });
      }
    });
  }
  eventHook(value: any) {}
  hookForAllEvents(value: any) {}
  getJsonRuleFiles(state: string, formGroup: FormGroup) {
    this.reactiveForm = formGroup;
    this.state = state;
    //Initialize counts on first call (reset for new loading cycle)
    if (PipBaseStateComponent.loadedStatesCount === 0) {
      PipBaseStateComponent.stopLoading = false;
      PipBaseStateComponent.totalStatesCount =
        this.baseRulesService.getApplicableStates().length;
    }
    this.baseRulesService.getAllRules(state).subscribe((allRules) => {
      const baseRuleQuery = this.baseRuleQueryBuilder();
      if (this.baseRulesService.savedBaseRules[state]) {
        this.baseRules = this.baseRulesService.savedBaseRules[state];
      } else {
        this.baseRules = this.baseRulesService.getRuleBySelectionId(
          allRules.baseRules,
          baseRuleQuery,
        );
      }
      this.textValues = allRules.textValues;
      this.controls = allRules.controls;
      if (formGroup) {
        const dynamicRuleQuery = this.dynamicRuleQueryBuilder();
        this.dynamicRules = this.baseRulesService.getRuleBySelectionId(
          allRules.dynamicRules,
          dynamicRuleQuery,
        )?.option;
        this.setupControlEvents();
        this.setupHookForDropDownData();
      } else {
        this.isReviewPage = true;
        this.baseRulesService.initialPDFDataDumpProp(
          allRules.controls,
          allRules.state,
        );
        this.baseRulesService.getPdfDataDumpPropForSummaryPage(
          this.baseRules,
          allRules.state,
        );
      }
      // Check and update loading progress
      PipBaseStateComponent.checkAndUpdateLoadingProgress(state);
      const loaderService =
        this.outstandingLoadingService || this.loadingService;
      if (
        PipBaseStateComponent.loadedStatesCount >=
        PipBaseStateComponent.totalStatesCount
      ) {
        loaderService.stop();
      }
    });
  }

  dynamicRuleQueryBuilder() {
    return '';
  }

  baseRuleQueryBuilder() {
    if (this.baseRulesService.getNonGarageStates().includes(this.state)) {
      return this.baseRulesService.states.find((s) => s.state === this.state)
        ?.defaultQuery;
    }
    return undefined;
  }
  static resetLoadingState() {
    PipBaseStateComponent.stopLoading = false;
    PipBaseStateComponent.loadedStatesCount = 0;
    PipBaseStateComponent.totalStatesCount = 0;
  }

  static checkAndUpdateLoadingProgress(stateName: string) {
    // Increment loaded states count
    PipBaseStateComponent.loadedStatesCount++;
    // Check if all states are loaded
    if (
      PipBaseStateComponent.loadedStatesCount >=
      PipBaseStateComponent.totalStatesCount
    ) {
      PipBaseStateComponent.stopLoading = true;
    }
  }
  setupHookForDropDownData() {
    this.controls = [
      ...this.controls.map((control) => {
        if (control.hasHookForData) {
          control = { ...control, data: [this.calculatePremium(control)] };
        }
        return control;
      }),
    ];
  }

  calculatePremium(c: Control): any {
    return [];
  }

  setValidation() {
    if (this.baseRules === null) {
      return;
    }
    const requiredRadios = Array.from(
      new Set(
        Object.entries(this.baseRules)
          .filter(
            ([key, value]) =>
              key.endsWith('_Radio') &&
              !value.includes('optional') &&
              value.includes('show'),
          )
          .map(([key]) => {
            const match = key.match(/_(.+_Radio)$/);
            return match ? match[1] : key;
          }),
      ),
    );

    const requiredDropDowns = Array.from(
      new Set(
        Object.entries(this.baseRules)
          .filter(
            ([key, value]) =>
              key.endsWith('_DropDown') &&
              !value.includes('optional') &&
              (value.includes('show') || value.includes('form')),
          )
          .map(([key]) => key),
      ),
    );
    const requiredInputs = Array.from(
      new Set(
        Object.entries(this.baseRules)
          .filter(
            ([key, value]) =>
              key.endsWith('_Input') &&
              value.includes('show') &&
              !value.includes('optional'),
          )
          .map(([key]) => key),
      ),
    );

    requiredInputs.forEach((input) => {
      const control = this.reactiveForm.get(input);
      if (control) {
        control.setValidators([Validators.required]);
      }
    });

    requiredRadios.forEach((radio) => {
      const radioControlName = radio.charAt(0).toLowerCase() + radio.slice(1);
      const control = this.reactiveForm.get(radioControlName);
      if (control) {
        control.setValidators([Validators.required]);
      }
    });
    requiredDropDowns.forEach((dropDown) => {
      const control = this.reactiveForm.get(dropDown);
      if (control) {
        control.setValidators([Validators.required]);
      }
    });

    this.reactiveForm.updateValueAndValidity();
  }

  setDynamicRules(control: string) {
    this.baseRules = {
      ...this.baseRules,
      ...this.dynamicRules.find((option) => option.select === control)
        .changeBaseRules,
    };
    this.baseRulesService.savedBaseRules[this.state] = this.baseRules;

    // Check if we should preserve existing values
    const resetControls =
      this.dynamicRules.find((option) => option.select === control)
        ?.resetControls || [];
    // Reset conflicting controls to prevent PDF conflicts
    resetControls.forEach((controlName) => {
      const formControl = this.reactiveForm.get(controlName);
      if (formControl) {
        formControl.clearValidators();
        formControl.reset();
        this.resetPremium();
      }
    });
    this.setValidation();
  }

  private resetPremium() {
    this.controls.forEach((controlJsonObj) => {
      if (
        controlJsonObj.formControlName.toLocaleLowerCase().includes('dropdown')
      ) {
        const controlObj = this.reactiveForm.get(
          controlJsonObj.formControlName,
        );
        if (controlObj.value === null) {
          controlJsonObj.selectedPremium = '';
        }
      }
    });
  }
}
